import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, MapPin, ArrowSquareOut, Sparkle, Funnel, FileText, CurrencyInr, ClockCounterClockwise, Spinner, UsersThree, Info, DownloadSimple, CaretDown, CaretUp, UploadSimple, CircleNotch, X } from '@phosphor-icons/react';
import { donorPredictionService } from '../../../services/donorPrediction.service';
import { projectService } from '../../../services/project.service';
import { alumniService } from '../../../services/alumni.service';
import type { AlumniProfile } from '../../../services/alumni.service';
import type { RankedDonor, DonorPredictionResponse, V3RankedAlumni, V3DonorPredictionResponse } from '../../../services/donorPrediction.service';
import { useCategories } from '../hooks/useCategories';
import { useHaptic } from '../../../hooks/useHaptic';
import { useSessionState } from '../../../hooks/useSessionState';
import { useScrollRestoration } from '../../../hooks/useScrollRestoration';
import { Button } from '../../../components/ui/Button';
import { ErrorBoundary } from '../../../components/ui/ErrorBoundary';
import { formatToCr, parseToNumber } from '../../../utils/currencyFormatter';
import { toast } from 'sonner';
import { TrainModelModal } from './TrainModelModal';
import { AlumniQuickViewSheet } from './AlumniQuickViewSheet';

const BUSINESS_SECTORS = [
  "MANUFACTURING/HEAVY MACHINERY/METALLURGY",
  "FMCG, CONSUMER GOODS AND RETAIL",
  "FINANCE, BANKING AND VENTURE CAPITAL",
  "ENTERTAINMENT, MEDIA & SPORTS",
  "TECHNOLOGY, HEALTHCARE",
  "STRATEGY CONSULTING & CORPORATE ADVISORY",
  "SOFTWARE, SAAS & CONSUMER TECH PLATFORM",
  "PUBLIC POLICY, GOVERNANCE & SOCIAL IMPACT",
  "INFRASTRUCTURE, CIVIL ENGG",
  "SEMICONDUCTORS, ELECTRONICS & ELECTRICAL EQUIPMENT",
  "AUTOMOTIVE & MOBILITY",
  "ENERGY, OIL & GAS & RENEWABLE ENERGY",
  "AEROSPACE, DEFENCE & SPACE",
  "PHARMA, BIOTECH & MEDICAL DEVICES",
  "CHEMICALS & SPECIALTY MATERIALS",
  "CLEAN TECH, CLIMATE & ENVIRONMENT",
  "EDUCATION & EDTECH",
  "AGRICULTURE, AGRITECH & FOOD PROCESSING",
  "ARCHITECTURE, DESIGN & CREATIVE SERVICES",
  "NON-PROFIT, PHILANTHROPY & DEVELOPMENT ORGANIZATIONS",
  "OTHER"
];

// The category/sub-category list previously lived here as a private,
// drifting copy. It's gone -- use the useCategories() hook, which reads
// the live list from GET /dashboard/categories.

const VALS_DIMENSIONS_LABELS: Record<string, string> = {
  iitm_affinity: 'IITM Affinity',
  research_advancement: 'Research Advancement',
  founder_innovation: 'Founder Innovation',
  legacy_recognition: 'Legacy Recognition',
  student_development: 'Student Development',
  social_national_impact: 'Social & National Impact',
  public_influence: 'Public Influence',
};

interface DonorPredictorViewProps {
  onBack: () => void;
}


export interface DonorResultCardProps {
  donor: RankedDonor | V3RankedAlumni;
  modelType: 'heuristic' | 'lightgbm';
  isExistingDonor?: boolean;
  onRowClick: (donorId: string) => void;
}

export type SortColumn = 'past_giving' | 'target_ask' | 'net_worth' | null;
export type SortDirection = 'asc' | 'desc';

export interface DonorResultTableHeaderProps {
  sortConfig: { column: SortColumn; direction: SortDirection };
  onSort: (column: SortColumn) => void;
}

/**
 * Parses a formatted currency string back into a crore-equivalent number for sorting.
 * Handles ranges like "₹50 Cr – ₹150 Cr" or "₹X Lakhs – ₹Y Lakhs" by using the
 * lower bound of the range. Supports em dash (–), en dash (—), and regular hyphen (-).
 * Returns null if the value cannot be parsed.
 */
function parseToCr(value: string | number | undefined | null): number | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'number') {
    // raw rupee number — convert to crore
    return value > 1e5 ? value / 1e7 : value;
  }

  // Split on any dash variant or "to" to separate range — take the first (lower) segment
  const firstSegment = String(value)
    .split(/\s*[–—\-]\s*|\s+to\s+/i)[0]
    .trim();

  // Strip currency symbols and commas
  const cleaned = firstSegment.replace(/[₹$,]/g, '').trim();

  // Match "<number> <unit?>"
  const match = cleaned.match(/^([\d.]+)\s*(cr|crore|lakh|lakhs|lac|lacs|l)?s?$/i);
  if (!match) return null;

  const num = parseFloat(match[1]);
  if (isNaN(num)) return null;

  const unit = (match[2] || '').toLowerCase();
  if (unit.startsWith('cr')) return num;
  if (unit.startsWith('l')) return num / 100;

  // No explicit unit: disambiguate by magnitude (raw rupees vs already in crore)
  return num > 1e5 ? num / 1e7 : num;
}

/** Sticky header row — small-caps labels matching the app's micro-label style. */
export const DonorResultTableHeader: React.FC<DonorResultTableHeaderProps> = ({ sortConfig, onSort }) => {
  const getSortIcon = (column: SortColumn) => {
    if (sortConfig.column !== column) return null;
    return sortConfig.direction === 'asc' ? <CaretUp size={12} weight="bold" className="ml-1 text-accent shrink-0" /> : <CaretDown size={12} weight="bold" className="ml-1 text-accent shrink-0" />;
  };

  return (
    <div className="hidden lg:flex items-center gap-4 px-5 py-2.5 border-b border-border-light bg-surface-hover">
      <span className="w-8 text-[10px] font-bold uppercase tracking-widest text-secondary shrink-0">#</span>
      <span className="flex-[2] text-[10px] font-bold uppercase tracking-widest text-secondary truncate">Donor</span>
      <span className="flex-[1.4] text-[10px] font-bold uppercase tracking-widest text-secondary truncate">Role @ Company</span>
      <button onClick={() => onSort('past_giving')} className="w-28 flex items-center justify-end text-right text-[10px] font-bold uppercase tracking-widest text-secondary hover:text-accent transition-colors cursor-pointer shrink-0">Past Giving {getSortIcon('past_giving')}</button>
      <button onClick={() => onSort('target_ask')} className="w-28 flex items-center justify-end text-right text-[10px] font-bold uppercase tracking-widest text-secondary hover:text-accent transition-colors cursor-pointer shrink-0">Target Ask {getSortIcon('target_ask')}</button>
      <button onClick={() => onSort('net_worth')} className="w-28 flex items-center justify-end text-right text-[10px] font-bold uppercase tracking-widest text-secondary hover:text-accent transition-colors cursor-pointer shrink-0">Net Worth {getSortIcon('net_worth')}</button>
      <span className="w-16 text-right text-[10px] font-bold uppercase tracking-widest text-secondary shrink-0">Score</span>
      <span className="w-6 shrink-0" />
    </div>
  );
};

export const DonorResultCard: React.FC<DonorResultCardProps> = ({
  donor,
  modelType,
  isExistingDonor,
  onRowClick,
}) => {
  const [expanded, setExpanded] = useState(false);

  const isV3 = modelType === 'lightgbm';
  const v3Donor = isV3 ? (donor as V3RankedAlumni) : null;
  const v1Donor = isV3 ? null : (donor as RankedDonor);

  // Score: V3 uses rank_score (negative float), V1 uses final_score (0–1)
  const score = isV3
    ? Math.max(0, Math.min(100, Math.round(((v3Donor!.rank_score + 5) / 5) * 100)))
    : Math.round((v1Donor!.final_score || 0) * 100);
  const hasHistory = isExistingDonor
    ?? (donor._full_profile?.monday_data && donor._full_profile.monday_data.length > 0);

  const vals = (donor._full_profile?.vals_framework as any) || {};
  const company = vals.company || donor._full_profile?.source_info?.company || (v3Donor?.company) || 'N/A';
  const role = vals.role || donor._full_profile?.source_info?.designation || 'N/A';
  const location = donor._full_profile?.linkedin_info?.location || donor._full_profile?.linkedin_info?.city || 'Unknown Location';
  const batch = donor._full_profile?.source_info?.batch || (v3Donor?.batch_year ? String(v3Donor.batch_year) : 'Unknown Batch');

  let paidSum = 0;

  if (donor._full_profile?.monday_data) {
    donor._full_profile.monday_data.forEach((m: any) => {
      const isPaid = m.paid === true;
      const amt = Number(m.amount) || 0;
      if (isPaid) {
        paidSum += amt;
      }
    });
  } else if (isV3 && v3Donor?.financial_indicators?.cumulative_giving) {
    paidSum = v3Donor.financial_indicators.cumulative_giving;
  } else if (donor._full_profile?.source_info?.cumulative_giving_to_iitm) {
    paidSum = Number(donor._full_profile.source_info.cumulative_giving_to_iitm) || 0;
  }

  const cumGivingPaidStr = paidSum > 0 ? formatToCr(String(paidSum)) : '0';

  // Target ask: V3 uses financial_indicators, V1 uses recommended_ask_inr
  const targetAsk = isV3
    ? (v3Donor?.financial_indicators?.recommended_ask_inr
        ? formatToCr(String(v3Donor.financial_indicators.recommended_ask_inr))
        : 'N/A')
    : formatToCr(v1Donor?.recommended_ask_inr) || 'N/A';

  // Net worth: V3 uses financial_indicators, V1 uses vals_framework from full profile
  const netWorthStr = isV3
    ? (v3Donor?.financial_indicators?.net_worth_usd_millions != null
        ? `$${v3Donor.financial_indicators.net_worth_usd_millions.toFixed(1)}M`
        : (vals.estimated_financial_capacity?.net_worth_inr || 'N/A'))
    : (vals.estimated_financial_capacity?.net_worth_inr || 'N/A');

  const askCr = parseToCr(isV3 ? v3Donor?.financial_indicators?.recommended_ask_inr : v1Donor?.recommended_ask_inr);
  const netWorthCr = parseToCr(netWorthStr);
  const utilizationPct =
    askCr !== null && netWorthCr !== null && netWorthCr > 0
      ? Math.min(Math.round((askCr / netWorthCr) * 1000) / 10, 100)
      : null;

  // Propensity tier maps to the app's semantic tokens, not raw Tailwind hues.
  const tier =
    score >= 75
      ? { var: 'var(--color-success)', label: 'High' }
      : score >= 50
        ? { var: 'var(--color-warning)', label: 'Medium' }
        : { var: 'var(--color-error)', label: 'Low' };

  // Score bars: V3 now mimics V1 labels (Alignment, Philanthropic, Capacity) for UI consistency
  const scoreBars = isV3
    ? [
        { label: 'Alignment', value: (v3Donor?.retrieval_cosine ?? 0) },
        { label: 'Philanthropic', value: Math.max(0, Math.min(1, (v3Donor?.rank_score ?? -5) + 5) / 5) },
        { label: 'Capacity', value: v3Donor?.financial_indicators?.net_worth_usd_millions ? Math.min(1, v3Donor.financial_indicators.net_worth_usd_millions / 50) : 0.5 },
      ]
    : [
        { label: 'Alignment', value: v1Donor?.alignment_score || ((vals as any)?.scores?.iitm_affinity?.score ?? (vals as any)?.iitm_affinity) },
        { label: 'Philanthropic', value: v1Donor?.philanthropic_score || ((vals as any)?.scores?.philanthropic_history?.score ?? (vals as any)?.philanthropic_history) },
        { label: 'Capacity', value: v1Donor?.capacity_score },
      ];

  const matchReason = isV3
    ? v3Donor?.primary_motivation
        ? `Strong alignment with ${v3Donor.primary_motivation} based on recent professional milestones.`
        : 'Algorithmic match based on profile heuristics and giving capacity.'
    : v1Donor?.match_reason || 'Algorithmic match based on profile heuristics and giving capacity.';

  return (
    <div className="border-b border-border-light last:border-b-0">
      {/* Main row */}
      <div
        onClick={() => onRowClick(donor.donor_id)}
        className="group flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 px-5 py-3.5 hover:bg-surface-hover transition-smooth cursor-pointer relative"
      >

        {/* Rank */}
        <span className="w-8 shrink-0 text-xs font-outfit font-bold text-secondary">#{donor.rank}</span>

        {/* Donor identity */}
        <div className="flex-[2] min-w-0 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-outfit font-bold text-sm text-primary group-hover:text-accent transition-smooth truncate">
              {donor.name}
            </span>
            <span
              className={`shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full border ${hasHistory
                  ? 'bg-surface-hover text-secondary border-border'
                  : 'bg-accent-light text-accent-active border-border-accent'
                }`}
            >
              {hasHistory ? 'Existing Donor' : 'New Prospect'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-medium text-secondary truncate">
            <span className="flex items-center gap-1 shrink-0"><MapPin size={11} /> {location}</span>
            <span className="text-border shrink-0">•</span>
            <span className="shrink-0">Batch {batch}</span>
          </div>
        </div>

        {/* Role @ Company — sales' primary lookup field */}
        <div className="flex-[1.4] min-w-0 text-xs font-semibold text-primary truncate" title={`${role} @ ${company}`}>
          {role} <span className="text-secondary font-normal">@ {company}</span>
        </div>

        {/* Past Giving */}
        <div className="w-full lg:w-28 flex lg:justify-end justify-between">
          <span className="lg:hidden text-[10px] font-bold text-secondary uppercase">Past Giving</span>
          <span className="text-sm font-outfit font-bold text-primary">{cumGivingPaidStr}</span>
        </div>

        {/* Target Ask — the actionable number, accent-highlighted */}
        <div className="w-full lg:w-28 flex lg:justify-end justify-between">
          <span className="lg:hidden text-[10px] font-bold text-secondary uppercase">Target Ask</span>
          <span className="text-sm font-outfit font-medium text-black">{targetAsk}</span>
        </div>

        {/* Net Worth — quieter, it's an estimate not a fact */}
        <div className="w-full lg:w-28 flex lg:justify-end justify-between">
          <span className="lg:hidden text-[10px] font-bold text-secondary uppercase">Net Worth</span>
          <span className="text-xs font-outfit font-medium text-secondary">{netWorthStr}</span>
        </div>

        {/* Propensity — compact number + tier dot, no big ring */}
        <div className="w-full lg:w-16 flex lg:justify-end justify-between items-center gap-1.5">
          <span className="lg:hidden text-[10px] font-bold text-secondary uppercase">Score</span>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tier.var }} title={`${tier.label} propensity`} />
          <span className="text-xs font-outfit font-bold text-primary">{score}</span>
        </div>

        {/* Expand toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="w-6 h-6 shrink-0 flex items-center justify-center rounded-md text-secondary hover:bg-surface hover:text-accent transition-smooth self-end lg:self-auto hover:cursor-pointer"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
        >
          <CaretDown size={13} weight="bold" className={`transition-smooth ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Expanded detail row: score breakdown, match reason, ask-to-capacity */}
      {expanded && (
        <div className="px-5 pb-4 pt-1 bg-surface-hover/50 animate-fade-in">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
            <div className="flex-1 space-y-2">
              {scoreBars.map(({ label, value }) => {
                const numVal = typeof value === 'number' ? value : parseFloat(value as any);
                const pct = isNaN(numVal) ? 0 : Math.round(numVal * (modelType === 'heuristic' ? 100 : 10));
                return (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-secondary w-20 shrink-0">{label}</span>
                    <div className="flex-1 bg-border-light h-1.5 rounded-full overflow-hidden">
                      <div className="bg-accent h-full rounded-full transition-smooth" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {utilizationPct !== null && (
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-[10px] font-bold text-secondary w-20 shrink-0">Ask / Worth</span>
                  <div className="flex-1 bg-border-light h-1.5 rounded-full overflow-hidden">
                    <div className="bg-accent-muted h-full rounded-full transition-smooth" style={{ width: `${utilizationPct}%` }} />
                  </div>
                  <span className="text-[10px] font-bold text-secondary shrink-0">{utilizationPct}%</span>
                </div>
              )}
            </div>

            <div className="flex-1 flex gap-2 items-start bg-surface rounded-xl p-3 border border-border-light">
              <Sparkle size={14} className="text-accent shrink-0 mt-0.5" weight="duotone" />
              <p className="text-[11px] font-medium text-secondary leading-relaxed">
                {matchReason}
              </p>
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); onRowClick(donor.donor_id); }}
              className="shrink-0 flex items-center gap-1 h-fit text-[10px] font-bold text-accent bg-accent-light hover:bg-accent hover:text-white px-3 py-1.5 rounded-full transition-smooth"
            >
              View Profile <ArrowSquareOut size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export function DonorPredictorView({ onBack }: DonorPredictorViewProps) {
  const { lightTap, mediumTap } = useHaptic();
  const { categoryMap } = useCategories();

  // Form states
  const [projectName, setProjectName] = useSessionState('donor-predictor:projectName', '');
  const [category, setCategory] = useSessionState('donor-predictor:category', 'Research');
  const [subCategory, setSubCategory] = useSessionState('donor-predictor:subCategory', '');
  const [summary, setSummary] = useSessionState('donor-predictor:summary', '');
  const [modelType, setModelType] = useSessionState<'heuristic' | 'lightgbm'>('donor-predictor:modelType', 'heuristic');
  const [sortConfig, setSortConfig] = useSessionState<{ column: SortColumn; direction: SortDirection }>('donor-predictor:sortConfig', { column: null, direction: 'desc' });

  // Model Training & Registry State
  const [isTrainModalOpen, setIsTrainModalOpen] = useState(false);
  const [activeModelVersion, setActiveModelVersion] = useState<string>('v3_baseline');

  // Document upload state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDraggingDoc, setIsDraggingDoc] = useState(false);
  const [isAnalyzingDoc, setIsAnalyzingDoc] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDocumentSelect = async (newFiles: FileList | null) => {
    if (!newFiles) return;
    const fileArray = Array.from(newFiles);
    setUploadedFiles(prev => [...prev, ...fileArray]);
  };

  const handleAnalyzeDocuments = async () => {
    if (uploadedFiles.length === 0) return;
    setIsAnalyzingDoc(true);
    try {
      const { ai_proposed } = await projectService.extractMetadataFromDocuments(uploadedFiles);
      if (ai_proposed?.project_name) setProjectName(ai_proposed.project_name);
      if (ai_proposed?.category) setCategory(ai_proposed.category);
      if (ai_proposed?.sub_category) setSubCategory(ai_proposed.sub_category);
      if (ai_proposed?.summary) setSummary(ai_proposed.summary);
      toast.success('Auto-filled project details from document');
      setIsUploadModalOpen(false);
      setUploadedFiles([]);
    } catch (err) {
      console.error('Failed to extract metadata:', err);
      toast.error('Failed to analyze document.');
    } finally {
      setIsAnalyzingDoc(false);
    }
  };

  // Search History State
  const [searchHistory, setSearchHistory] = useState<any[]>([]);

  // Global Frequent Donors State
  const [globalFrequentDonors, setGlobalFrequentDonors] = useState<import('../../../services/donorPrediction.service').GlobalFrequentDonor[]>([]);

  // Fetch search history and global top on mount
  useEffect(() => {
    donorPredictionService.getPredictionHistory()
      .then(res => {
        if (res && res.history) {
          setSearchHistory(res.history);
        }
      })
      .catch(err => {
        console.error('Failed to fetch prediction history:', err);
      });

    donorPredictionService.getGlobalFrequentDonors()
      .then(res => {
        if (res && res.donors) {
          setGlobalFrequentDonors(res.donors);
        }
      })
      .catch(err => {
        console.error('Failed to fetch global frequent donors:', err);
      });

    donorPredictionService.listModels()
      .then(res => {
        if (res && res.active_version) {
          setActiveModelVersion(res.active_version);
        }
      })
      .catch(err => {
        console.error('Failed to fetch active model version:', err);
      });
  }, []);

  // Weights states for Heuristic Matcher
  const [finalAlignment, setFinalAlignment] = useSessionState('donor-predictor:finalAlignment', 0.35);
  const [finalPhilanthropic, setFinalPhilanthropic] = useSessionState('donor-predictor:finalPhilanthropic', 0.40);
  const [finalCapacity, setFinalCapacity] = useSessionState('donor-predictor:finalCapacity', 0.25);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const [philHistory, setPhilHistory] = useSessionState('donor-predictor:philHistory', 0.50);
  const [philGiving, setPhilGiving] = useSessionState('donor-predictor:philGiving', 0.15);
  const [philAffinity, setPhilAffinity] = useSessionState('donor-predictor:philAffinity', 0.35);

  const [capNetWorth, setCapNetWorth] = useSessionState('donor-predictor:capNetWorth', 0.60);
  const [capPropensity, setCapPropensity] = useSessionState('donor-predictor:capPropensity', 0.40);
  const [includedVals, setIncludedVals] = useSessionState<string[]>('donor-predictor:includedVals', Object.keys(VALS_DIMENSIONS_LABELS));

  // Normalization logic for Final Weights
  const sumFinal = finalAlignment + finalPhilanthropic + finalCapacity;
  const normAlignment = sumFinal > 0 ? finalAlignment / sumFinal : 0.3333;
  const normPhilanthropic = sumFinal > 0 ? finalPhilanthropic / sumFinal : 0.3333;
  const normCapacity = sumFinal > 0 ? finalCapacity / sumFinal : 0.3333;

  // Normalization logic for Philanthropic Weights
  const sumPhil = philHistory + philGiving + philAffinity;
  const normPhilHistory = sumPhil > 0 ? philHistory / sumPhil : 0.3333;
  const normPhilGiving = sumPhil > 0 ? philGiving / sumPhil : 0.3333;
  const normPhilAffinity = sumPhil > 0 ? philAffinity / sumPhil : 0.3333;

  // Normalization logic for Capacity Weights
  const sumCap = capNetWorth + capPropensity;
  const normCapNetWorth = sumCap > 0 ? capNetWorth / sumCap : 0.5;
  const normCapPropensity = sumCap > 0 ? capPropensity / sumCap : 0.5;

  const resetWeightsToDefault = () => {
    lightTap();
    setFinalAlignment(0.35);
    setFinalPhilanthropic(0.40);
    setFinalCapacity(0.25);
    setPhilHistory(0.50);
    setPhilGiving(donorHistoryFilter === 'new' ? 0 : 0.15);
    setPhilAffinity(0.35);
    setCapNetWorth(0.60);
    setCapPropensity(0.40);
    setIncludedVals(Object.keys(VALS_DIMENSIONS_LABELS));
  };

  // Filter states
  const [donorHistoryFilter, setDonorHistoryFilter] = useSessionState<'all' | 'new' | 'old'>('donor-predictor:donorHistoryFilter', 'all');

  // React to the user *changing* the filter, not to mounting — on mount this would
  // overwrite the giving weight restored from the previous visit.
  const didMountHistoryFilter = useRef(false);
  useEffect(() => {
    if (!didMountHistoryFilter.current) {
      didMountHistoryFilter.current = true;
      return;
    }
    if (donorHistoryFilter === 'new') {
      setPhilGiving(0);
    } else {
      setPhilGiving(0.15);
    }
  }, [donorHistoryFilter]);

  const [budgetLimit, setBudgetLimit] = useSessionState<string>('donor-predictor:budgetLimit', '');
  const [numDonors, setNumDonors] = useSessionState<number>('donor-predictor:numDonors', 8);
  const [batchFilterInput, setBatchFilterInput] = useSessionState<string>('donor-predictor:batchFilterInput', '');
  const [locationFilterInput, setLocationFilterInput] = useSessionState<string>('donor-predictor:locationFilterInput', '');
  const [industryFilterInput, setIndustryFilterInput] = useSessionState<string>('donor-predictor:industryFilterInput', '');

  // UI States
  const [isPredicting, setIsPredicting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [predictionStep, setPredictionStep] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [computedCount, setComputedCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentProgressMessage, setCurrentProgressMessage] = useState<string>('');

  // Prediction response
  const [rawResponse, setRawResponse] = useSessionState<DonorPredictionResponse | null>('donor-predictor:rawResponse', null);
  const [v3RawResponse, setV3RawResponse] = useSessionState<V3DonorPredictionResponse | null>('donor-predictor:v3RawResponse', null);
  const [enrichedDonors, setEnrichedDonors] = useSessionState<(RankedDonor | V3RankedAlumni)[]>('donor-predictor:enrichedDonors', []);
  const [filteredDonors, setFilteredDonors] = useSessionState<(RankedDonor | V3RankedAlumni)[]>('donor-predictor:filteredDonors', []);
  // Track which donors are existing (true) vs new prospects (false) for V3 badge display
  const [enrichedDonorIsExisting, setEnrichedDonorIsExisting] = useSessionState<Record<string, boolean>>('donor-predictor:enrichedDonorIsExisting', {});

  useScrollRestoration(filteredDonors.length > 0);

  // Donor shown in the profile sheet; null when it's closed.
  const [quickViewDonorId, setQuickViewDonorId] = useState<string | null>(null);

  // Full alumni profiles by donor id: prefetched for the top of the list and
  // kept from the profile sheet, so a donor is fetched once.
  const fullProfileCacheRef = useRef<Record<string, AlumniProfile>>({});

  // Loading step simulation intervals
  useEffect(() => {
    if (!isPredicting) return;

    const interval = setInterval(() => {
      setPredictionStep((prev) => {
        if (prev < 4) return prev + 1;
        return prev;
      });
    }, 1200);

    return () => clearInterval(interval);
  }, [isPredicting]);

  // Handle client-side filters
  const panelDonors = useMemo(() => {
    if (!globalFrequentDonors.length || !enrichedDonors.length) return [];
    
    const top10Global = globalFrequentDonors.slice(0, 10);
    const globalIds = new Set(top10Global.map(g => g.donor_id));
    const intersection = enrichedDonors.filter(d => globalIds.has(d.donor_id)).map(d => {
      const globalInfo = top10Global.find(g => g.donor_id === d.donor_id);
      return { 
        ...d, 
        global_rank: top10Global.indexOf(globalInfo!) + 1,
        global_score: globalInfo?.global_score
      };
    });
    // Sort by current rank
    return intersection.sort((a, b) => (a.rank || 999) - (b.rank || 999));
  }, [globalFrequentDonors, enrichedDonors]);

  useEffect(() => {
    if (!enrichedDonors.length) {
      setFilteredDonors([]);
      return;
    }

    let result = [...enrichedDonors];

    // Filter out panel donors from the main list
    const panelDonorIds = new Set(panelDonors.map(d => d.donor_id));
    result = result.filter(d => !panelDonorIds.has(d.donor_id));

    // Filter by New vs Old
    // V3: use server-side provenance map (Head A = existing, Head B = new prospects)
    // V1: use monday_data presence (client-side heuristic)
    if (donorHistoryFilter === 'new') {
      result = result.filter((d) => {
        if (d.donor_id in enrichedDonorIsExisting) return enrichedDonorIsExisting[d.donor_id] === false;
        const hasHistory = d._full_profile?.monday_data && d._full_profile.monday_data.length > 0;
        return !hasHistory;
      });
    } else if (donorHistoryFilter === 'old') {
      result = result.filter((d) => {
        if (d.donor_id in enrichedDonorIsExisting) return enrichedDonorIsExisting[d.donor_id] === true;
        const hasHistory = d._full_profile?.monday_data && d._full_profile.monday_data.length > 0;
        return hasHistory;
      });
    }

    // Filter by budget target
    if (budgetLimit && !isNaN(Number(budgetLimit))) {
      const budgetInLakhs = Number(budgetLimit);
      result = result.filter((d) => {
        // V3: check financial_indicators.recommended_ask_inr
        const v3Ask = (d as any).financial_indicators?.recommended_ask_inr;
        const askStr = v3Ask != null ? String(v3Ask) : ((d as any).recommended_ask_inr || '');
        const parsedAskNumber = parseToNumber(askStr);
        const parsedAsk = parsedAskNumber ? parsedAskNumber / 100000 : 0;
        return parsedAsk >= budgetInLakhs * 0.1;
      });
    }

    // Apply sorting
    if (sortConfig.column) {
      result.sort((a, b) => {
        let aVal = 0;
        let bVal = 0;

        if (sortConfig.column === 'target_ask') {
          const getAsk = (d: any) => d.financial_indicators?.recommended_ask_inr ?? d.recommended_ask_inr;
          aVal = parseToCr(getAsk(a)) || 0;
          bVal = parseToCr(getAsk(b)) || 0;
        } else if (sortConfig.column === 'net_worth') {
          const getNetWorth = (d: any) => {
            if (d.financial_indicators?.net_worth_usd_millions != null)
              return d.financial_indicators.net_worth_usd_millions * 830; // USD → INR crore rough conversion
            return parseToCr((d._full_profile?.vals_framework as any)?.estimated_financial_capacity?.net_worth_inr) || 0;
          };
          aVal = getNetWorth(a);
          bVal = getNetWorth(b);
        } else if (sortConfig.column === 'past_giving') {
          const getPaidSum = (donor: any) => {
            if (enrichedDonorIsExisting[donor.donor_id] != null) {
              // V3: use financial_indicators.cumulative_giving
              return donor.financial_indicators?.cumulative_giving ?? 0;
            }
            const hasHist = donor._full_profile?.monday_data && donor._full_profile.monday_data.length > 0;
            let sum = 0;
            if (hasHist) {
              donor._full_profile!.monday_data!.forEach((m: any) => {
                if (m.paid === true) sum += Number(m.amount) || 0;
              });
            } else if (donor._full_profile?.source_info?.cumulative_giving_to_iitm) {
              sum = Number(donor._full_profile.source_info.cumulative_giving_to_iitm) || 0;
            }
            return sum;
          };
          aVal = getPaidSum(a);
          bVal = getPaidSum(b);
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredDonors(result);
  }, [enrichedDonors, enrichedDonorIsExisting, donorHistoryFilter, budgetLimit, sortConfig, panelDonors]);


  const prefetchTopDonors = async (donorsToPrefetch: any[]) => {
    try {
      const top10 = donorsToPrefetch.slice(0, 10);
      const enriched10 = await Promise.all(
        top10.map(async (donor) => {
          if (fullProfileCacheRef.current[donor.donor_id]) {
            return { ...donor, _full_profile: fullProfileCacheRef.current[donor.donor_id] };
          }
          try {
            const profileRes = await alumniService.getAlumniList({ q: donor.donor_id });
            const fullProfile = profileRes.alumni?.[0];
            if (fullProfile) {
              fullProfileCacheRef.current[donor.donor_id] = fullProfile;
              return { ...donor, _full_profile: fullProfile };
            }
          } catch (e) {
            console.error(`Prefetch failed for ${donor.donor_id}`, e);
          }
          return donor;
        })
      );

      setEnrichedDonors(prev => prev.map(d => {
        const preloaded = enriched10.find(e => e.donor_id === d.donor_id);
        return preloaded ? preloaded : d;
      }));
    } catch (e) {
      console.error("Prefetch process failed", e);
    }
  };

  const handlePredict = async (e?: React.FormEvent, overrideHistoryFilter?: 'all' | 'new' | 'old') => {
    if (e) e.preventDefault();
    mediumTap();

    if (!projectName.trim()) {
      setErrorMsg('Please specify a project name.');
      return;
    }

    setIsPredicting(true);
    setErrorMsg(null);
    setRawResponse(null);
    setEnrichedDonors([]);
    setPredictionStep(1);
    setComputedCount(0);
    setTotalCount(0);
    setCurrentProgressMessage('Establishing connection to predictor...');

    const historyFilter = overrideHistoryFilter || donorHistoryFilter;
    const mappedDonorType = historyFilter === 'old' ? 'existing' : historyFilter;

    const requestData = {
      cause: {
        cause_name: projectName,
        category,
        sub_category: subCategory || undefined,
        summary: summary,
      },
      donor_type: mappedDonorType as 'all' | 'existing' | 'new',
      filters: {
        batches: batchFilterInput ? batchFilterInput.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        locations: locationFilterInput ? locationFilterInput.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        industries: industryFilterInput ? industryFilterInput.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        included_vals_dimensions: includedVals,
      },
      final_weights: {
        alignment: normAlignment,
        philanthropic: normPhilanthropic,
        capacity: normCapacity,
      },
      philanthropic_weights: {
        philanthropic_history: normPhilHistory,
        cumulative_giving: normPhilGiving,
        category_affinity: normPhilAffinity,
      },
      capacity_weights: {
        net_worth: normCapNetWorth,
        propensity: normCapPropensity,
      },
    };

    try {
      if (modelType === 'lightgbm') {
        // ── V3 Two-Head LambdaRank ────────────────────────────────────────────
        const v3Request = {
          project_id: `PROJ-${Date.now()}`,
          cause_name: projectName,
          category,
          sub_category: subCategory || undefined,
          objective: summary || undefined,
          top_k: 50,  // fetch more than displayed; tabs will slice
          include_sensitive_financials: true,
        };
        const v3Response = await donorPredictionService.rankDonorsV3(v3Request);
        setV3RawResponse(v3Response);
        setRawResponse(null);

        // Combine both lists; track provenance for badge & Head label
        const existingWithFlag: V3RankedAlumni[] = v3Response.existing_donors;
        const prospectsWithFlag: V3RankedAlumni[] = v3Response.new_prospects;
        const allV3 = [...existingWithFlag, ...prospectsWithFlag];

        const isExistingMap: Record<string, boolean> = {};
        existingWithFlag.forEach(d => { isExistingMap[d.donor_id] = true; });
        prospectsWithFlag.forEach(d => { isExistingMap[d.donor_id] = false; });
        setEnrichedDonorIsExisting(isExistingMap);

        // Profiles are fetched lazily on row click — no bulk N+1 fetch here.
        setEnrichedDonors(allV3);
        prefetchTopDonors(allV3);
      } else {
        // ── V1 Heuristic (WebSocket) ─────────────────────────────────────────
        const response = await donorPredictionService.rankDonorsHeuristic(requestData, (current, total, msg) => {
          setComputedCount(current);
          setTotalCount(total);
          if (msg) {
            setCurrentProgressMessage(msg);
          } else if (current > 0 && total > 0) {
            setCurrentProgressMessage(`Evaluating motives and parameters for donor profiles...`);
          }
        });

        setRawResponse(response);
        setV3RawResponse(null);

        const top10Global = globalFrequentDonors.slice(0, 10);
        const globalIds = new Set(top10Global.map(g => g.donor_id));

        const allRanked = response.ranked_donors || [];
        const panelCandidates = allRanked.filter(d => globalIds.has(d.donor_id));
        const mainCandidates = allRanked.filter(d => !globalIds.has(d.donor_id)).slice(0, numDonors);

        const topDonors = [...panelCandidates, ...mainCandidates];

        // Profiles are fetched lazily on row click — no bulk N+1 fetch here.
        setEnrichedDonors(topDonors);
        prefetchTopDonors(topDonors);
      }
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || 'An error occurred while evaluating donor propensity.');
    } finally {
      setIsPredicting(false);
      setPredictionStep(0);
    }
  };

  const handleLoadNext10 = async () => {
    // Load more is only meaningful for V1 heuristic (V3 returns all 50 upfront)
    if (!rawResponse || !rawResponse.ranked_donors) return;
    lightTap();
    setIsLoadingNext(true);
    try {
      const top10Global = globalFrequentDonors.slice(0, 10);
      const globalIds = new Set(top10Global.map(g => g.donor_id));
      
      const allRankedMain = rawResponse.ranked_donors.filter(d => !globalIds.has(d.donor_id));
      const currentMainLoadedCount = enrichedDonors.filter(d => !globalIds.has(d.donor_id)).length;
      
      const nextDonors = allRankedMain.slice(currentMainLoadedCount, currentMainLoadedCount + 10);
      if (nextDonors.length === 0) return;

      // Profiles are fetched lazily on row click — no bulk N+1 fetch here.
      setEnrichedDonors(prev => [...prev, ...nextDonors]);
      prefetchTopDonors(nextDonors);
      setNumDonors(prev => prev + 10); // update numDonors so filter logic holds
    } catch (err) {
      console.error('Error loading next donors:', err);
    } finally {
      setIsLoadingNext(false);
    }
  };

  const handleBackClick = () => {
    lightTap();
    onBack();
  };

  const resetForm = () => {
    lightTap();
    setRawResponse(null);
    setV3RawResponse(null);
    setEnrichedDonors([]);
    setFilteredDonors([]);
    setEnrichedDonorIsExisting({});
  };

  const handleRowClick = (donorId: string) => {
    lightTap();
    setQuickViewDonorId(donorId);
  };

  // Keep what the profile sheet fetched, so reopening a donor is instant and
  // the list's filters and sorts can use the full profile.
  const handleProfileLoaded = (donorId: string, fullProfile: AlumniProfile) => {
    fullProfileCacheRef.current[donorId] = fullProfile;
    setEnrichedDonors(prev =>
      prev.map(d => d.donor_id === donorId ? { ...d, _full_profile: fullProfile } : d)
    );
  };

  const handleSort = (column: SortColumn) => {
    lightTap();
    setSortConfig((prev) => {
      if (prev.column === column) {
        if (prev.direction === 'desc') return { column, direction: 'asc' };
        return { column: null, direction: 'desc' };
      }
      return { column, direction: 'desc' };
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let donorIds: string[];
      if (v3RawResponse) {
        // V3: export whichever list is currently visible
        const histFilter = donorHistoryFilter;
        const combined: V3RankedAlumni[] = histFilter === 'new'
          ? v3RawResponse.new_prospects
          : histFilter === 'old'
            ? v3RawResponse.existing_donors
            : [...v3RawResponse.existing_donors, ...v3RawResponse.new_prospects];
        donorIds = combined.slice(0, 500).map(d => d.donor_id);
      } else if (rawResponse?.ranked_donors) {
        donorIds = rawResponse.ranked_donors.slice(0, 500).map(d => d.donor_id);
      } else {
        return;
      }
      const blob = await donorPredictionService.exportDonorsLightGBM(donorIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `predicted_donors_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Donors exported successfully!');
    } catch (err) {
      console.error('Export failed:', err);
      setErrorMsg('Failed to export donors. Please try again.');
      toast.error('Failed to export donors.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6 font-sans">
        {/* Header & Breadcrumb */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-[10px] text-slate-405 font-bold uppercase tracking-wider">
            <button onClick={handleBackClick} className="hover:text-accent transition-colors cursor-pointer focus:outline-none">Dashboard</button>
            <span>/</span>
            <span className="text-slate-600 font-extrabold">Donor Predictor</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-outfit font-extrabold text-3xl text-slate-900 tracking-tight leading-none">Donor Predictor</h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  Model: {activeModelVersion}
                </span>
              </div>
              <p className="text-slate-450 text-xs font-semibold mt-2">Utilize predictive modeling to find alumni with the highest giving propensity.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setIsTrainModalOpen(true)}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-3.5 py-2 rounded-xl shadow-xs"
              >
                <Sparkle size={14} weight="bold" />
                Train Model
              </Button>
              <Button
                onClick={handleBackClick}
                variant="outline"
                className="flex items-center gap-1.5"
              >
                <ArrowLeft size={14} weight="bold" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>

        {isPredicting && (
          <div className="bg-white border border-slate-200/50 rounded-2xl p-10 shadow-xs flex flex-col items-center justify-center text-center space-y-8 min-h-[380px]">
            {/* Spinning/pulsing logo container */}
            <div className="relative flex items-center justify-center">
              <div className="w-16 h-16 border-4 border-accent/20 border-t-accent rounded-full animate-spin absolute mb-5" />
            </div>

            {modelType === 'heuristic' ? (
              <div className="space-y-6 w-full max-w-md">
                <div className="space-y-2">
                  <h3 className="font-outfit font-extrabold text-xl text-slate-900 leading-none">
                    {totalCount === 0 ? 'Analyzing Cause details...' : 'Scoring matching alumni...'}
                  </h3>
                  <p className="text-xs text-slate-450 font-semibold leading-relaxed">
                    {currentProgressMessage || 'Scoring motivations, giving capacity, and affinity alignment.'}
                  </p>
                </div>

                {totalCount > 0 && (
                  <div className="space-y-3 pt-2">
                    {/* Progress details */}
                    <div className="flex justify-between items-center text-xs font-bold text-slate-600">
                      <span>Evaluated: {computedCount} / {totalCount} Donors</span>
                      <span className="text-accent font-extrabold">{Math.round((computedCount / totalCount) * 100)}%</span>
                    </div>

                    {/* Progress Bar Container */}
                    <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden border border-slate-200/40 p-[2px] shadow-inner">
                      <div
                        className="bg-accent h-full rounded-full transition-all duration-300 shadow-sm"
                        style={{ width: `${(computedCount / totalCount) * 100}%` }}
                      />
                    </div>

                    {/* Remaining/To go metrics */}
                    <div className="flex justify-between text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      <span>Completed: {computedCount}</span>
                      <span>To go: {totalCount - computedCount} remaining</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 max-w-md">
                <h3 className="font-outfit font-extrabold text-xl text-slate-900 leading-none">
                  {predictionStep === 1 && 'Extracting database features...'}
                  {predictionStep === 2 && 'Running predictive algorithms...'}
                  {predictionStep === 3 && 'Evaluating donor psychographics...'}
                  {predictionStep >= 4 && 'Tuning scoring confidence...'}
                </h3>
                <p className="text-xs text-slate-450 font-semibold leading-relaxed">
                  {predictionStep === 1 && 'Scanning historical logs, Scraped LinkedIn profiles, and IITM convocation databases.'}
                  {predictionStep === 2 && 'Tuning pre-trained LightGBM Booster and classification trees.'}
                  {predictionStep === 3 && 'Analyzing motivations, estimated net worth logs, and category affinity matching.'}
                  {predictionStep >= 4 && 'Applying giving priority tiers and preparing recommendations.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Input specifications form */}
        {!isPredicting && !rawResponse && (
          <form onSubmit={handlePredict} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Spec Fields */}
            <div className="lg:col-span-2 bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between w-full">
                <div>
                  <h2 className="font-outfit font-extrabold text-lg text-slate-900 flex items-center gap-2">
                    <FileText size={20} className="text-accent" />
                    Project Specifications
                  </h2>
                  <p className="text-slate-450 text-[11px] font-semibold mt-1">Describe your fundraising project details to evaluate matching donors.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(true)}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-accent bg-accent/10 hover:bg-accent hover:text-white px-3 py-1.5 rounded-lg transition-colors shrink-0"
                >
                  <UploadSimple size={14} weight="bold" />
                  Auto-fill from PDF
                </button>
              </div>
              <hr className="border-slate-100" />

              <div className="space-y-3">


                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Project Name *</label>
                  </div>

                  <input
                    type="text"
                    required
                    placeholder="e.g. Green Hydrogen direct photoelectrolysis..."
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-semibold"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Category</label>
                    <select
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        setSubCategory('');
                      }}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 transition-smooth font-semibold"
                    >
                      <option value="Research">Research</option>
                      <option value="Student">Student</option>
                      <option value="Faculty">Faculty</option>
                      <option value="Infrastructure">Infrastructure</option>
                      <option value="Inovation and Entrepreneurship">Innovation & Entrepreneurship</option>
                      <option value="Institutional Alumini Engagement">Institutional Alumni Engagement</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Sub-category</label>
                    <select
                      value={subCategory}
                      onChange={(e) => setSubCategory(e.target.value)}
                      disabled={!category}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 transition-smooth font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">-- Select Sub-Category --</option>
                      {category && categoryMap[category] &&
                        categoryMap[category].map((subName) => (
                          <option key={subName} value={subName}>{subName}</option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Project Summary / Objective</label>
                  <textarea
                    rows={4}
                    placeholder="Provide a detailed description of target metrics, impacts, and the core objective..."
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-medium resize-none"
                  />
                </div>
              </div>
              {searchHistory.length > 0 && (
                <div className="pt-5 mt-6 border-t border-slate-100/80">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <ClockCounterClockwise size={14} weight="bold" />
                    Recent Searches
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {searchHistory.slice(0, 10).map((hist, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          lightTap();
                          setProjectName(hist.cause_name || '');
                          setCategory(hist.category || 'Research');
                          setSubCategory(hist.sub_category || '');
                          setSummary(hist.summary || '');
                        }}
                        className="group flex flex-col items-start text-left bg-slate-50 border border-slate-200/60 hover:bg-white hover:border-accent hover:shadow-sm focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-xl p-3 transition-all cursor-pointer overflow-hidden relative"
                      >
                        <div className="absolute top-0 right-0 w-16 h-full bg-gradient-to-l from-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none" />
                        <span className="text-xs font-bold text-slate-800 group-hover:text-accent truncate w-full z-0 transition-colors">
                          {hist.cause_name || 'Unnamed Project'}
                        </span>
                        <span className="text-[10px] text-slate-450 font-semibold mt-1 truncate w-full z-0">
                          {hist.category || 'Research'}{hist.sub_category ? ` • ${hist.sub_category}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>


            {/* Model & advanced configuration selectors */}
            <div className="bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-6 flex flex-col justify-between">
              <div className="space-y-4">
                <h2 className="font-outfit font-extrabold text-lg text-slate-900 flex items-center gap-2">
                  Model Configuration
                </h2>
                <p className="text-slate-450 text-[11px] font-semibold">Configure algorithm model and filters.</p>
                <hr className="border-slate-100" />

                <div className="space-y-4">
                  {/* Model type toggle */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Predictive Algorithm</label>
                    <div className="grid grid-cols-2 gap-2 p-1 bg-slate-50 border border-slate-200/60 rounded-xl">
                      <button
                        type="button"
                        onClick={() => setModelType('heuristic')}
                        className={`py-2 text-xs font-bold rounded-lg transition-smooth cursor-pointer ${modelType === 'heuristic'
                            ? 'bg-white text-accent shadow-xs'
                            : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        Heuristic Model
                      </button>
                      <button
                        type="button"
                        onClick={() => setModelType('lightgbm')}
                        className={`py-2 text-xs font-bold rounded-lg transition-smooth cursor-pointer ${modelType === 'lightgbm'
                            ? 'bg-white text-accent shadow-xs'
                            : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        LightGBM ML
                      </button>
                    </div>
                  </div>

                  {/* New vs old donors filter */}
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Giving History (Donor Type)</label>
                    <div className="grid grid-cols-3 gap-2 p-1 bg-slate-50 border border-slate-200/60 rounded-xl">
                      <button
                        type="button"
                        onClick={() => setDonorHistoryFilter('all')}
                        className={`py-2 text-[10px] font-bold rounded-lg transition-smooth cursor-pointer ${donorHistoryFilter === 'all'
                            ? 'bg-white text-accent shadow-xs'
                            : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setDonorHistoryFilter('new')}
                        className={`py-2 text-[10px] font-bold rounded-lg transition-smooth cursor-pointer ${donorHistoryFilter === 'new'
                            ? 'bg-white text-accent shadow-xs'
                            : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        New
                      </button>
                      <button
                        type="button"
                        onClick={() => setDonorHistoryFilter('old')}
                        className={`py-2 text-[10px] font-bold rounded-lg transition-smooth cursor-pointer ${donorHistoryFilter === 'old'
                            ? 'bg-white text-accent shadow-xs'
                            : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        Existing
                      </button>
                    </div>
                  </div>

                  {/* Batch filter */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      Target Batches (Comma-separated)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 1980, 1981..."
                      value={batchFilterInput}
                      onChange={(e) => setBatchFilterInput(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-semibold"
                    />
                  </div>

                  {/* Location filter */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      Target Locations (Comma-separated)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Chennai, Bengaluru, San..."
                      value={locationFilterInput}
                      onChange={(e) => setLocationFilterInput(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-semibold"
                    />
                  </div>

                  {/* Industry filter */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      Business Sectors
                    </label>
                    <select
                      value={industryFilterInput}
                      onChange={(e) => setIndustryFilterInput(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 transition-smooth font-semibold"
                    >
                      <option value="">-- All Sectors --</option>
                      {BUSINESS_SECTORS.map((sector) => (
                        <option key={sector} value={sector}>{sector}</option>
                      ))}
                    </select>
                  </div>

                  {/* Target Budget filter */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <CurrencyInr size={12} />
                      Target Project Budget (Lakhs)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 25..."
                      value={budgetLimit}
                      onChange={(e) => setBudgetLimit(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-semibold"
                    />
                  </div>

                  {/* Number of Donors to Fetch */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Number of Donors to Fetch
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={numDonors}
                      onChange={(e) => setNumDonors(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/5 focus:bg-white outline-none rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 transition-smooth font-semibold"
                    />
                  </div>

                  {/* Dynamic weights adjustments (Heuristic only) */}
                  {modelType === 'heuristic' && (
                    <div className="space-y-4 pt-4 border-t border-slate-100">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            Final Weights Configuration
                          </label>
                          <button
                            type="button"
                            onClick={resetWeightsToDefault}
                            className="text-[10px] font-bold text-accent hover:underline cursor-pointer focus:outline-none"
                          >
                            Reset Defaults
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex justify-between text-[10px] font-bold text-slate-500">
                              <span className="flex items-center gap-1">Vals Alignment <span title="Cosine similarity of VALS vectors" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                              <span>{Math.round(normAlignment * 100)}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={finalAlignment}
                              onChange={(e) => setFinalAlignment(parseFloat(e.target.value))}
                              className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                            />
                          </div>

                          <div className="flex flex-col gap-1">
                            <div className="flex justify-between text-[10px] font-bold text-slate-500">
                              <span className="flex items-center gap-1">Philanthropic <span title="Historical giving and category affinity" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                              <span>{Math.round(normPhilanthropic * 100)}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={finalPhilanthropic}
                              onChange={(e) => setFinalPhilanthropic(parseFloat(e.target.value))}
                              className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                            />
                          </div>

                          <div className="flex flex-col gap-1">
                            <div className="flex justify-between text-[10px] font-bold text-slate-500">
                              <span className="flex items-center gap-1">Capacity <span title="Financial ability to give" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                              <span>{Math.round(normCapacity * 100)}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={finalCapacity}
                              onChange={(e) => setFinalCapacity(parseFloat(e.target.value))}
                              className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                            />
                          </div>

                        </div>
                      </div>

                      {/* Advanced Settings Toggle */}
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={() => { lightTap(); setShowAdvanced(!showAdvanced); }}
                          className="w-full flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider hover:text-accent transition-colors cursor-pointer"
                        >
                          <span>Advanced Settings</span>
                          <span className="text-xs">{showAdvanced ? '−' : '+'}</span>
                        </button>

                        {showAdvanced && (
                          <div className="space-y-4 pt-3 mt-2 border-t border-slate-100/60">
                            {/* Philanthropic Weights */}
                            <div className="space-y-2">
                              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">
                                Philanthropic Weights
                              </label>

                              <div className="flex flex-col gap-1">
                                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                  <span className="flex items-center gap-1">Philanthropic Score <span title="Innate generosity based on VALS profile" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                                  <span>{Math.round(normPhilHistory * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={philHistory}
                                  onChange={(e) => setPhilHistory(parseFloat(e.target.value))}
                                  className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                                />
                              </div>

                              <div className="flex flex-col gap-1">
                                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                  <span className="flex items-center gap-1">Cumulative Giving <span title="Total past donations to IITM" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                                  <span>{Math.round(normPhilGiving * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={philGiving}
                                  onChange={(e) => setPhilGiving(parseFloat(e.target.value))}
                                  className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                                />
                              </div>

                              <div className="flex flex-col gap-1">
                                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                  <span className="flex items-center gap-1">Interest Affinity <span title="Semantic match of stated interests and causes" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                                  <span>{Math.round(normPhilAffinity * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={philAffinity}
                                  onChange={(e) => setPhilAffinity(parseFloat(e.target.value))}
                                  className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                                />
                              </div>
                            </div>

                            {/* Capacity Weights */}
                            <div className="space-y-2 pt-2 border-t border-slate-100/40">
                              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">
                                Capacity Weights
                              </label>

                              <div className="flex flex-col gap-1">
                                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                  <span className="flex items-center gap-1">Net Worth <span title="Estimated net worth from profile" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                                  <span>{Math.round(normCapNetWorth * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={capNetWorth}
                                  onChange={(e) => setCapNetWorth(parseFloat(e.target.value))}
                                  className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                                />
                              </div>

                              <div className="flex flex-col gap-1">
                                <div className="flex justify-between text-[9px] font-bold text-slate-500">
                                  <span className="flex items-center gap-1">Giving Propensity <span title="Likelihood to give based on engagement" className="flex items-center"><Info size={12} weight="bold" className="text-slate-400 cursor-help hover:text-accent transition-colors" /></span></span>
                                  <span>{Math.round(normCapPropensity * 100)}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={capPropensity}
                                  onChange={(e) => setCapPropensity(parseFloat(e.target.value))}
                                  className="w-full h-1 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-accent"
                                />
                              </div>
                            </div>

                            {/* VALS Framework selection */}
                            <div className="space-y-2 pt-2 border-t border-slate-100/40">
                              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">
                                Included VALS Dimensions
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                {Object.entries(VALS_DIMENSIONS_LABELS).map(([key, label]) => (
                                  <label key={key} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={includedVals.includes(key)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setIncludedVals(prev => [...prev, key]);
                                        } else {
                                          setIncludedVals(prev => prev.filter(k => k !== key));
                                        }
                                      }}
                                      className="w-3 h-3 rounded border-slate-300 text-accent focus:ring-accent accent-accent cursor-pointer"
                                    />
                                    <span className="text-[10px] font-bold text-slate-500 group-hover:text-slate-700 transition-colors">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {errorMsg && (
                <div className="p-3 border border-rose-100 rounded-xl bg-rose-50 text-[11px] font-bold text-rose-600 leading-snug">
                  {errorMsg}
                </div>
              )}

              <Button
                type="submit"
                className="w-full flex items-center justify-center gap-2 mt-4 py-3"
              >
                <Sparkle size={16} weight="bold" />
                Run Predictive Scoring
              </Button>
            </div>
          </form>
        )}

        {/* Prediction Results Board */}
        {!isPredicting && (rawResponse || v3RawResponse) && (
          <div className="space-y-6">
            {/* Top overview stats & project score mapping */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Rationale and Details summary */}
              <div className="lg:col-span-2 bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[9px] font-bold text-accent bg-accent-light border border-accent/20 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      {modelType === 'lightgbm' ? 'LightGBM Predictor' : 'Heuristic VALS Matcher'}
                    </span>
                    <h2 className="font-outfit font-extrabold text-2xl text-slate-900 mt-2">{rawResponse?.cause_name || v3RawResponse?.cause_name || projectName}</h2>
                  </div>
                  <Button onClick={resetForm} variant="outline" className="py-1.5 px-3 text-[10px]">
                    Adjust Parameters
                  </Button>
                </div>
                <hr className="border-slate-100" />
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Project Input Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 font-medium bg-slate-50 p-4 border border-slate-100 rounded-xl">
                      <div>
                        <span className="block text-[9px] text-slate-400 uppercase font-bold mb-0.5">Project Name</span>
                        {projectName}
                      </div>
                      <div>
                        <span className="block text-[9px] text-slate-400 uppercase font-bold mb-0.5">Category</span>
                        {category} {subCategory ? `> ${subCategory}` : ''}
                      </div>
                      <div className="col-span-2">
                        <span className="block text-[9px] text-slate-400 uppercase font-bold mb-0.5">Summary</span>
                        {summary || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Scoring Rationale</h4>
                    <p className="text-xs text-slate-600 font-medium leading-relaxed bg-slate-50 p-4 border border-slate-100 rounded-xl">
                      {rawResponse?.scoring_rationale || 'Evaluated motivation categories using AI prompts and donor alignment algorithms.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Project VALS dimensions progress */}
              <div className="bg-white border border-slate-200/50 rounded-2xl p-6 shadow-xs space-y-4">
                <h3 className="font-outfit font-bold text-slate-900 text-sm">Calculated Project Motivation</h3>
                <hr className="border-slate-100" />
                <div className="space-y-2.5 overflow-y-auto pr-1">
                  {rawResponse?.cause_vals && Object.entries(rawResponse.cause_vals).map(([key, val]) => {
                    const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                    const pct = Math.round((val as number) * 10);
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex justify-between text-[10px] font-bold">
                          <span className="text-slate-600">{formattedKey}</span>
                          <span className="text-slate-900">{pct}%</span>
                        </div>
                        <div className="w-full bg-slate-50 h-1.5 rounded-full overflow-hidden border border-slate-100">
                          <div className="bg-accent h-full rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Ranked result table */}
            <div className="flex flex-col xl:flex-row gap-6 items-start">
              
              {/* Main List Column */}
              <div className="flex-1 w-full bg-white border border-slate-200/50 rounded-2xl p-5 shadow-xs space-y-4 overflow-hidden">
                <div className="flex flex-col lg:flex-row gap-4 items-center justify-between border-b border-slate-100 pb-3">
                  <h3 className="font-outfit font-extrabold text-lg text-slate-900 shrink-0">Ranked Donors Evaluation</h3>

                  {/* Filtration badges inside results dashboard */}
                  <div className="flex flex-wrap items-center gap-3 lg:gap-4">
                    <div className="flex items-center gap-1 text-[10px] text-slate-450 font-bold uppercase tracking-wider">
                      <Funnel size={14} className="text-slate-400" />
                      Filters:
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => {
                          setDonorHistoryFilter('all');
                          handlePredict(undefined, 'all');
                        }}
                        className={`text-[9px] font-extrabold px-2.5 py-1 rounded-full border transition-smooth cursor-pointer ${donorHistoryFilter === 'all' ? 'bg-accent-light border-accent/20 text-accent' : 'bg-slate-50 border-slate-200 text-slate-500'
                          }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => {
                          setDonorHistoryFilter('new');
                          handlePredict(undefined, 'new');
                        }}
                        className={`text-[9px] font-extrabold px-2.5 py-1 rounded-full border transition-smooth cursor-pointer ${donorHistoryFilter === 'new' ? 'bg-accent-light border-accent/20 text-accent' : 'bg-slate-50 border-slate-200 text-slate-500'
                          }`}
                      >
                        New Donors
                      </button>
                      <button
                        onClick={() => {
                          setDonorHistoryFilter('old');
                          handlePredict(undefined, 'old');
                        }}
                        className={`text-[9px] font-extrabold px-2.5 py-1 rounded-full border transition-smooth cursor-pointer ${donorHistoryFilter === 'old' ? 'bg-accent-light border-accent/20 text-accent' : 'bg-slate-50 border-slate-200 text-slate-500'
                          }`}
                      >
                        Existing
                      </button>
                    </div>
                    <button
                      onClick={handleExport}
                      disabled={isExporting || filteredDonors.length === 0}
                      className="flex items-center gap-1 text-[10px] font-extrabold px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {isExporting ? <Spinner className="animate-spin" size={14} /> : <DownloadSimple size={14} weight="bold" />}
                      {isExporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                  </div>
                </div>

                {filteredDonors.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 font-medium text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <UsersThree size={32} weight="duotone" className="text-slate-300" />
                      <span>No matching donors satisfy the current filtration criteria.</span>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-200/50 overflow-hidden">
                    <DonorResultTableHeader sortConfig={sortConfig} onSort={handleSort} />
                    {filteredDonors.map((donor) => (
                      <DonorResultCard
                        key={donor.donor_id}
                        donor={donor}
                        modelType={modelType}
                        isExistingDonor={enrichedDonorIsExisting[donor.donor_id]}
                        onRowClick={handleRowClick}
                      />
                    ))}
                  </div>
                )}

                {/* Load Next 10 — only for V1 heuristic (V3 returns all results upfront) */}
                {filteredDonors.length > 0 && rawResponse?.ranked_donors && enrichedDonors.length < rawResponse.ranked_donors.length && (
                  <div className="flex justify-center pt-4 border-t border-slate-100">
                    <Button
                      onClick={handleLoadNext10}
                      variant="outline"
                      disabled={isLoadingNext}
                      className="text-xs font-bold py-2 px-6 shadow-sm hover:shadow"
                    >
                      {isLoadingNext ? 'Loading...' : 'Load Next 10'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Side Panel: Global Frequent Donors in Results */}
              {panelDonors.length > 0 && (
                <div className="w-full xl:w-80 shrink-0 bg-white border border-slate-200/50 rounded-2xl p-5 shadow-xs flex flex-col gap-4 sticky top-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                    <Sparkle size={18} weight="fill" className="text-slate-700" />
                    <h3 className="font-outfit font-extrabold text-[15px] text-slate-900 leading-tight">
                      Overall Top Performers
                    </h3>
                  </div>
                  <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                    These donors are present in your current results and are also among the most frequently recommended donors overall.
                  </p>
                  <div className="space-y-3 mt-2">
                    {panelDonors.map((donor: any) => (
                      <div 
                        key={donor.donor_id} 
                        onClick={() => handleRowClick(donor.donor_id)}
                        className="bg-slate-50 border border-slate-200/60 rounded-xl p-3 shadow-xs hover:border-slate-300 hover:shadow-md transition-all cursor-pointer group"
                      >
                        <div className="flex justify-between items-start mb-1.5">
                          <span className="font-outfit font-bold text-[13px] text-slate-900 group-hover:text-accent transition-colors truncate pr-2">
                            {donor.name}
                          </span>
                          <span className="shrink-0 bg-slate-200 text-slate-700 text-[9px] font-extrabold px-2 py-0.5 rounded-full border border-slate-300">
                            Overall #{donor.global_rank}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 font-medium">
                          <span>Current Rank: <strong className="text-slate-800">#{donor.rank}</strong></span>
                          <span>Score: <strong className="text-slate-800">{Math.round(donor.final_score * 100)}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <AlumniQuickViewSheet
          donorId={quickViewDonorId}
          onClose={() => setQuickViewDonorId(null)}
          initialProfile={quickViewDonorId ? enrichedDonors.find((d) => d.donor_id === quickViewDonorId)?._full_profile : undefined}
          onProfileLoaded={handleProfileLoaded}
        />

        {isUploadModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
            <div
              className="bg-white border border-slate-200/80 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                    <UploadSimple className="w-4 h-4" weight="fill" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm">Upload Documents</h3>
                    <p className="text-[11px] text-slate-500 font-medium">Extract project details automatically</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  disabled={isAnalyzingDoc}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-full hover:bg-slate-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingDoc(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDraggingDoc(false); }}
                  onDrop={(e) => { e.preventDefault(); setIsDraggingDoc(false); if (e.dataTransfer.files?.length) handleDocumentSelect(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer transition-all ${
                    isDraggingDoc
                      ? 'border-accent bg-accent/5'
                      : 'border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.pptx,.txt,image/*"
                    className="hidden"
                    onChange={(e) => handleDocumentSelect(e.target.files)}
                  />
                  <div className="flex flex-col items-center gap-1">
                    <UploadSimple className="w-5 h-5 text-accent" />
                    <p className="text-xs font-semibold text-slate-700">Click to upload or drag a project document</p>
                    <p className="text-[9px] text-slate-400">We'll analyze it to auto-fill the project details.</p>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="space-y-1.5 max-h-32 overflow-y-auto pt-2">
                    {uploadedFiles.map((file, idx) => (
                      <div
                        key={`${file.name}-${idx}`}
                        className="flex items-center justify-between bg-slate-50 border border-slate-200/60 px-3 py-1.5 rounded-xl text-xs"
                      >
                        <div className="flex items-center gap-2 min-w-0 pr-2">
                          <FileText className="w-4 h-4 text-accent shrink-0" />
                          <span className="font-medium text-slate-700 truncate">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setUploadedFiles((prev) => prev.filter((_, i) => i !== idx)); }}
                          disabled={isAnalyzingDoc}
                          className="text-slate-400 hover:text-rose-500 transition-colors p-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  disabled={isAnalyzingDoc}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAnalyzeDocuments}
                  disabled={isAnalyzingDoc || uploadedFiles.length === 0}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all"
                >
                  {isAnalyzingDoc ? (
                    <>
                      <CircleNotch className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" weight="fill" />
                      Auto-fill Details
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <TrainModelModal
          isOpen={isTrainModalOpen}
          onClose={() => setIsTrainModalOpen(false)}
          activeModelVersion={activeModelVersion}
          onActiveModelChanged={(newVersion) => setActiveModelVersion(newVersion)}
        />
      </div>
    </ErrorBoundary>
  );
}